import { Instagram } from 'lucide-react';

import { AppStoreBadge } from '@/components/app-store-badge';
import { BabysitterMode } from '@/components/babysitter-mode';
import { DynamicText } from '@/components/dynamic-text';
import { ScreenshotShowcase } from '@/components/screenshot-showcase';

/** Bramble on Instagram. Exported so the footer can pick it up if social links land there. */
export const INSTAGRAM_URL = 'https://instagram.com/brambleapp.xyz';

export default function HomePage() {
  return (
    <>
      <Hero />
      <ScreenshotShowcase />
      <BabysitterMode />
    </>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <VineBackdrop />

      <div className="container relative grid gap-14 pb-8 pt-20 sm:pt-28 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center lg:pb-16">
        <div>
          <p className="kicker animate-rise">
            <DynamicText path="hero.kicker">Tracking is free &middot; ad-free &middot; iPhone</DynamicText>
          </p>

          <h1
            className="mt-6 animate-rise text-[clamp(3.6rem,13vw,8.5rem)] font-semibold leading-[0.86] tracking-[-0.03em]"
            style={{ animationDelay: '80ms' }}
          >
            <DynamicText path="hero.title">Bramble</DynamicText>
          </h1>

          <p
            className="mt-7 max-w-xl animate-rise text-[clamp(1.1rem,2vw,1.35rem)] leading-snug text-foreground/85"
            style={{ animationDelay: '160ms' }}
          >
            <DynamicText path="hero.tagline">
              The privacy-first baby tracker. No account required to get started*, no ads, and no
              tracking. Ever.
            </DynamicText>
          </p>

          <p
            className="mt-2 text-xs text-muted-foreground animate-rise"
            style={{ animationDelay: '200ms' }}
          >
            <DynamicText path="hero.disclaimer">
              *Bramble Sharing requires an account and a $4.99/month subscription. All on-device
              tracking stays free.
            </DynamicText>
          </p>

          <div
            className="mt-8 flex animate-rise flex-wrap items-center gap-5"
            style={{ animationDelay: '240ms' }}
          >
            <AppStoreBadge />
            <a
              href={INSTAGRAM_URL}
              // Leaving the site, so the usual pair: `noopener` to deny the opened page a
              // handle back to this one, `noreferrer` to keep the referrer off it.
              target="_blank"
              rel="noopener noreferrer"
              // Icon-only, so the accessible name has to come from the label. Lucide does
              // not mark its SVGs aria-hidden, so the icon does that itself below.
              aria-label="Bramble on Instagram"
              className="inline-flex size-11 items-center justify-center rounded-full border border-border/70 text-muted-foreground transition-[transform,color,border-color] duration-200 hover:-translate-y-0.5 hover:border-berry hover:text-berry active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Instagram className="size-5" strokeWidth={1.75} aria-hidden="true" />
            </a>
          </div>
        </div>

        <ClaimsPanel />
      </div>
    </section>
  );
}

/**
 * Three short claims about what the app does, read as a sequence: log it, shape
 * it to how you track, then see what it adds up to. The privacy argument these
 * replaced now lives in the tagline above, so it isn't repeated here.
 *
 * Every claim has to stay literally true of the shipped app — no roadmap. The
 * "Highly configurable" title is deliberately broad enough to cover theming
 * later, but its body only names settings that exist today.
 */
const CLAIMS = [
  {
    n: '01',
    title: 'Quick logging',
    body: 'Record feeds, pumping, diapers, sleep, potty, health, and milestones with a few taps.',
  },
  {
    n: '02',
    title: 'Highly configurable',
    body: "Hide the things you don't need to track, set a custom theme, and control your units.",
  },
  {
    n: '03',
    title: 'Analyze trends',
    body: 'See WHO growth percentiles, your child\'s top foods, and how sleep and potty training change over time.',
  },
];

function ClaimsPanel() {
  return (
    <ul className="relative animate-rise space-y-0" style={{ animationDelay: '400ms' }}>
      {CLAIMS.map((claim, i) => (
        <li
          key={claim.n}
          className="group relative border-t border-border py-6 last:border-b sm:py-7"
          style={{ animationDelay: `${440 + i * 90}ms` }}
        >
          <div className="flex gap-5">
            <span className="font-mono text-xs leading-7 text-berry">{claim.n}</span>
            <div>
              <h2 className="font-display text-[1.3rem] font-semibold leading-snug">
                <DynamicText path={`claims.${claim.n}.title`}>{claim.title}</DynamicText>
              </h2>
              <p className="mt-1.5 text-[0.98rem] leading-relaxed text-muted-foreground">
                <DynamicText path={`claims.${claim.n}.body`}>{claim.body}</DynamicText>
              </p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Decorative bramble stem that draws itself in behind the wordmark. */
function VineBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg
        viewBox="0 0 1200 600"
        preserveAspectRatio="xMidYMid slice"
        className="absolute -right-24 -top-24 h-[130%] w-[110%] text-berry/25 sm:-right-10"
      >
        <path
          d="M-40 520 C 180 470, 260 330, 430 300 S 700 330, 840 220 S 1080 90, 1260 120"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="1400"
          className="animate-draw-vine"
        />
        <path
          d="M-40 600 C 220 560, 340 430, 520 420 S 820 470, 980 350 S 1180 250, 1260 260"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="1400"
          className="animate-draw-vine"
          style={{ animationDelay: '500ms' }}
          opacity="0.6"
        />
        <g className="animate-drift">
          <circle cx="430" cy="300" r="7" className="fill-berry/40" />
          <circle cx="448" cy="288" r="4.5" className="fill-berry/30" />
          <circle cx="840" cy="220" r="9" className="fill-berry/30" />
          <circle cx="858" cy="234" r="5" className="fill-berry/20" />
          <circle cx="520" cy="420" r="6" className="fill-leaf/30" />
        </g>
      </svg>

      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-background" />
    </div>
  );
}
